/**
 * Text-line rules PH-03, PH-15 and PH-16 (W2-12b checker-rules; DESIGN.md §7.6, §6.8; rpe-format.md F§9.3, F§13;
 * rendering.md R§3.6, R§11 item 23).
 *
 * Phira interpolates text with its string tween (F§9.3), in this order: both ends contain `%P%` -> numeric counter;
 * both empty -> ""; end empty -> typed erase; start empty -> typed reveal; the end extends the start -> typewriter;
 * the start extends the end -> backspace; otherwise the start is held. Reveal and erase slice the text by the eased
 * progress, so an easing that overshoots asks for more characters than exist and Phira panics (PH-03, fatal); the
 * typewriter cases slice the same way (PH-16, warning: the crash is not verified). After the LAST text key Phira shows
 * the raw end string, so a trailing `%P%` is visible literally, and a counter whose remainder is not a number shows 0
 * (PH-15; an empty remainder, as in "%P%" -> "%P%100", is the counter's intended 0 and is not reported).
 *
 * Only lines on which Phira uses the text track are checked (text lines: `line.png` without brush keys); text kept
 * on other lines is never evaluated by Phira (PH-24 reports it).
 *
 * Worker-safe: imports only src/core/**.
 */

import {
  FIELD,
  PERCENT_P,
  easeArg,
  easeParams,
  easedExtent,
  emitProblem,
  fixRef,
  fmtBeat,
  isCounterNumber,
  kindOf,
  lineLabel,
  linesToCheck,
  msg,
  problemId,
  propTracks,
  revealFix,
  safeEaseFor,
  stripPercentP,
  textExcerpt,
  textSegmentCase,
  textSegmentOverflow,
  trackKeys,
  type TextSegmentCase,
} from '../../../core/check/geometry';
import type { CheckContext, CheckRule } from '../../../core/check/types';
import { propAppliesTo } from '../../../core/model/props';
import type { Key, LineId } from '../../../core/model/types';

interface TextSegment {
  readonly key: Key;
  readonly next: Key;
  readonly x: string;
  readonly y: string;
  readonly kind: TextSegmentCase;
}

/** Text keys of the lines Phira draws text for, as tweened segments (hold segments never interpolate). */
function textTracks(ctx: CheckContext, line: LineId): Key[][] {
  if (!propAppliesTo('text', kindOf(ctx, line))) return [];
  return propTracks(ctx, line, 'text').map((t) => trackKeys(ctx, t.id));
}

function segments(keys: readonly Key[]): TextSegment[] {
  const out: TextSegment[] = [];
  for (let i = 0; i + 1 < keys.length; i++) {
    const key = keys[i];
    const next = keys[i + 1];
    if (key === undefined || next === undefined || key.ease.kind === 'hold') continue;
    const x = String(key.vOut);
    const y = String(next.vIn);
    out.push({ key, next, x, y, kind: textSegmentCase(x, y) });
  }
  return out;
}

function label(ctx: CheckContext, line: LineId): string {
  return lineLabel(ctx.doc, ctx.idx, line);
}

/** Shared body of PH-03 and PH-16: segments of the given cases whose eased progress overflows the text. */
function checkOvershoot(
  ctx: CheckContext,
  only: LineId | undefined,
  rule: 'PH-03' | 'PH-16',
  cases: readonly TextSegmentCase[],
): void {
  const what = rule === 'PH-03' ? 'rules.ph03What' : 'rules.ph16What';
  const why = rule === 'PH-03' ? 'rules.ph03Why' : 'rules.ph16Why';
  for (const line of linesToCheck(ctx, only)) {
    for (const keys of textTracks(ctx, line)) {
      for (const seg of segments(keys)) {
        if (!cases.includes(seg.kind)) continue;
        const extent = easedExtent(seg.key.ease);
        const extra = textSegmentOverflow(seg.kind, seg.x, seg.y, extent);
        if (extra <= 0) continue;
        const safe = safeEaseFor(seg.key.ease);
        emitProblem(ctx, {
          id: problemId(rule, 'key', seg.key.id),
          message: msg(what, {
            case: seg.kind,
            line: label(ctx, line),
            beat: fmtBeat(seg.key.t),
            ...easeParams(seg.key.ease),
            from: textExcerpt(seg.x),
            to: textExcerpt(seg.y),
          }),
          why: msg(why, { extra }),
          at: { line, beat: seg.key.t, keys: [seg.key.id], tracks: [seg.key.track], field: FIELD.segmentEase },
          fixes: [
            fixRef('problems.fix.setEase', msg('rules.fixUseEase', easeParams(safe)), {
              segments: [seg.key.id],
              ease: easeArg(safe),
            }),
            revealFix(msg('rules.fixGoTo'), seg.key.t, { line, keys: [seg.key.id] }),
          ],
        });
      }
    }
  }
}

// ---------------------------------------------------------------------------------------------------------------
// PH-03 type-on / erase segment with an overshooting easing (Phira panics)
// ---------------------------------------------------------------------------------------------------------------

const ph03: CheckRule = {
  id: 'PH-03',
  category: 'phira',
  severity: 'fatal',
  level: 'guided',
  scope: 'line',
  deps: ['motion', 'lines'],
  defaultEnabled: true,
  title: msg('rules.ph03Title'),
  check(ctx, line) {
    checkOvershoot(ctx, line, 'PH-03', ['reveal', 'erase']);
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-16 extend / shrink segment with an overshooting easing (may crash)
// ---------------------------------------------------------------------------------------------------------------

const ph16: CheckRule = {
  id: 'PH-16',
  category: 'phira',
  severity: 'warning',
  level: 'guided',
  scope: 'line',
  deps: ['motion', 'lines'],
  defaultEnabled: true,
  title: msg('rules.ph16Title'),
  check(ctx, line) {
    checkOvershoot(ctx, line, 'PH-16', ['extend', 'shrink']);
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-15 %P% shown literally after the last key, or a counter that is not a number
// ---------------------------------------------------------------------------------------------------------------

const ph15: CheckRule = {
  id: 'PH-15',
  category: 'phira',
  severity: 'warning',
  level: 'guided',
  scope: 'line',
  deps: ['motion', 'lines'],
  defaultEnabled: true,
  title: msg('rules.ph15Title'),
  check(ctx, only) {
    for (const line of linesToCheck(ctx, only)) {
      for (const keys of textTracks(ctx, line)) {
        const last = keys[keys.length - 1];
        // After the last key Phira returns the raw end string of the last event: the last key's leave value.
        if (last !== undefined && String(last.vOut).includes(PERCENT_P)) {
          const plain = stripPercentP(String(last.vOut));
          emitProblem(ctx, {
            id: problemId('PH-15', 'key', `${String(last.id)}-tail`),
            message: msg('rules.ph15Tail', {
              line: label(ctx, line),
              beat: fmtBeat(last.t),
              text: textExcerpt(String(last.vOut)),
            }),
            why: msg('rules.ph15TailWhy'),
            at: { line, beat: last.t, keys: [last.id], tracks: [last.track], field: FIELD.keyValue },
            fixes: [
              fixRef('problems.fix.setKeyValue', msg('rules.fixPlainTail', { text: textExcerpt(plain) }), {
                key: last.id,
                part: 'out',
                value: plain,
              }),
              revealFix(msg('rules.fixGoTo'), last.t, { line, keys: [last.id] }),
            ],
          });
        }
        for (const seg of segments(keys)) {
          const xHas = seg.x.includes(PERCENT_P);
          const yHas = seg.y.includes(PERCENT_P);
          if (seg.kind === 'counter') {
            const xs = stripPercentP(seg.x);
            const ys = stripPercentP(seg.y);
            // An empty remainder is the counter's intended 0 ("%P%" -> "%P%100"); a label or stray text is not.
            const bad = !isCounterNumber(xs) ? xs : !isCounterNumber(ys) ? ys : null;
            if (bad === null) continue;
            emitProblem(ctx, {
              id: problemId('PH-15', 'key', `${String(seg.key.id)}-number`),
              message: msg('rules.ph15NotNumber', {
                line: label(ctx, line),
                beat: fmtBeat(seg.key.t),
                from: textExcerpt(seg.x),
                to: textExcerpt(seg.y),
                bad: textExcerpt(bad),
              }),
              why: msg('rules.ph15NotNumberWhy'),
              at: {
                line,
                beat: seg.key.t,
                keys: [seg.key.id, seg.next.id],
                tracks: [seg.key.track],
                field: FIELD.keyValue,
              },
              fixes: [revealFix(msg('rules.fixGoTo'), seg.key.t, { line, keys: [seg.key.id] })],
            });
          } else if (seg.kind === 'switch' && xHas !== yHas) {
            // Only one end is a counter: Phira holds the start (without %P%) until the end key.
            const fixKey = xHas ? seg.next : seg.key;
            const part = xHas ? 'in' : 'out';
            const value = `${PERCENT_P}${xHas ? seg.y : seg.x}`;
            emitProblem(ctx, {
              id: problemId('PH-15', 'key', `${String(seg.key.id)}-onesided`),
              message: msg('rules.ph15OneSided', {
                line: label(ctx, line),
                beat: fmtBeat(seg.key.t),
                from: textExcerpt(seg.x),
                to: textExcerpt(seg.y),
              }),
              why: msg('rules.ph15OneSidedWhy', { shown: textExcerpt(stripPercentP(seg.x)) }),
              at: {
                line,
                beat: seg.key.t,
                keys: [seg.key.id, seg.next.id],
                tracks: [seg.key.track],
                field: FIELD.keyValue,
              },
              fixes: [
                fixRef('problems.fix.setKeyValue', msg('rules.fixAddPercentP', { text: textExcerpt(value) }), {
                  key: fixKey.id,
                  part,
                  value,
                }),
                revealFix(msg('rules.fixGoTo'), seg.key.t, { line, keys: [seg.key.id] }),
              ],
            });
          }
        }
      }
    }
  },
};

const rules: readonly CheckRule[] = [ph03, ph15, ph16];

export default rules;
